import React, { useState } from 'react';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Badge,
    Avatar,
    Menu,
    MenuItem,
    Chip,
    Alert,
    Snackbar,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Storage as FacilitiesIcon,
    Analytics as AnalyticsIcon,
    Settings as SettingsIcon,
    Help as HelpIcon,
    Notifications as NotificationsIcon,
    AccountCircle as AccountIcon,
    ChevronLeft as ChevronLeftIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Upload as UploadIcon,
    Assessment as InspectionReportsIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';

const drawerWidth = 280;

const navigationItems = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/',
    },
    {
        id: 'facilities',
        label: 'Facilities',
        icon: <FacilitiesIcon />,
        path: '/facilities',
    },
    {
        id: 'csv-upload',
        label: 'CSV Upload',
        icon: <UploadIcon />,
        path: '/csv-upload',
    },
    {
        id: 'inspection-reports',
        label: 'Inspection Reports',
        icon: <InspectionReportsIcon />,
        path: '/inspection-reports',
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: <SettingsIcon />,
        path: '/settings',
    },
];

const Layout = ({ children, currentPage = 'dashboard', onNavigate }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { state, apiActions } = useApp();
    const { notifications, runningAnalyses, apiAvailable, systemStatus } = state;

    // Local state
    const [mobileOpen, setMobileOpen] = useState(false);
    const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleAccountMenuOpen = (event) => {
        setAccountMenuAnchor(event.currentTarget);
    };

    const handleAccountMenuClose = () => {
        setAccountMenuAnchor(null);
    };

    const handleNavigate = (path) => {
        onNavigate?.(path);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleNotificationClose = (notificationId) => {
        apiActions.removeNotification(notificationId);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'warning':
                return <WarningIcon color="warning" />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    const getNotificationSeverity = (type) => {
        switch (type) {
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            default:
                return 'info';
        }
    };

    // Calculate running analyses count
    const runningAnalysesCount = Object.keys(runningAnalyses).length;

    const drawer = (
        <Box>
            {/* Logo/Header */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 40, height: 40 }}>
                        <AnalyticsIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
                            KiddieScraping
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Phase 3 Dashboard
                        </Typography>
                    </Box>
                </Box>
                {isMobile && (
                    <IconButton onClick={handleDrawerToggle}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}
            </Box>

            <Divider />

            {/* System Status */}
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        System Status
                    </Typography>
                    <Chip
                        size="small"
                        label={apiAvailable ? 'Online' : 'Offline'}
                        color={apiAvailable ? 'success' : 'error'}
                        variant="outlined"
                    />
                </Box>

                {systemStatus && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Total Facilities
                            </Typography>
                            <Typography variant="caption" fontWeight="medium">
                                {systemStatus.total_facilities || 0}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Running Analyses
                            </Typography>
                            <Badge badgeContent={runningAnalysesCount} color="primary">
                                <Typography variant="caption" fontWeight="medium">
                                    {systemStatus.running_analyses || 0}
                                </Typography>
                            </Badge>
                        </Box>
                    </Box>
                )}
            </Box>

            <Divider />

            {/* Navigation */}
            <List sx={{ px: 1 }}>
                {navigationItems.map((item) => (
                    <ListItem key={item.id} disablePadding>
                        <ListItemButton
                            selected={currentPage === item.id}
                            onClick={() => handleNavigate(item.path)}
                            sx={{
                                borderRadius: 2,
                                mx: 1,
                                mb: 0.5,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        backgroundColor: 'primary.main',
                                    },
                                },
                            }}
                        >
                            <ListItemIcon sx={{
                                color: currentPage === item.id ? 'inherit' : 'text.secondary',
                                minWidth: 40
                            }}>
                                {item.id === 'facilities' && runningAnalysesCount > 0 ? (
                                    <Badge badgeContent={runningAnalysesCount} color="secondary">
                                        {item.icon}
                                    </Badge>
                                ) : (
                                    item.icon
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: currentPage === item.id ? 600 : 400,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Divider sx={{ mt: 'auto' }} />

            {/* Footer */}
            <List sx={{ px: 1 }}>
                <ListItem disablePadding>
                    <ListItemButton sx={{ borderRadius: 2, mx: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <HelpIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Help"
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            {/* App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 1,
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {navigationItems.find(item => item.id === currentPage)?.label || 'Dashboard'}
                    </Typography>

                    {/* Notifications */}
                    <IconButton color="inherit" sx={{ mr: 1 }}>
                        <Badge badgeContent={notifications.length} color="error">
                            <NotificationsIcon />
                        </Badge>
                    </IconButton>

                    {/* Account Menu */}
                    <IconButton
                        color="inherit"
                        onClick={handleAccountMenuOpen}
                    >
                        <AccountIcon />
                    </IconButton>
                    <Menu
                        anchorEl={accountMenuAnchor}
                        open={Boolean(accountMenuAnchor)}
                        onClose={handleAccountMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={handleAccountMenuClose}>
                            <ListItemIcon>
                                <AccountIcon fontSize="small" />
                            </ListItemIcon>
                            Profile
                        </MenuItem>
                        <MenuItem onClick={handleAccountMenuClose}>
                            <ListItemIcon>
                                <SettingsIcon fontSize="small" />
                            </ListItemIcon>
                            Settings
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleAccountMenuClose}>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Navigation Drawer */}
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                {/* Desktop drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    minHeight: '100vh',
                    backgroundColor: 'background.default',
                }}
            >
                <Toolbar />
                {children}
            </Box>

            {/* Notifications */}
            {notifications.map((notification) => (
                <Snackbar
                    key={notification.id}
                    open={true}
                    autoHideDuration={notification.type === 'error' ? null : 6000}
                    onClose={() => handleNotificationClose(notification.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => handleNotificationClose(notification.id)}
                        severity={getNotificationSeverity(notification.type)}
                        variant="filled"
                        icon={getNotificationIcon(notification.type)}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            ))}
        </Box>
    );
};

export default Layout; 